/*
 * Copyright (c) Sureify Labs 2020.
 * This source code is licensed under MIT License.
 * @author jayendrasharan <jayendra@sureify.com>
 * Created at: 03/05/20, 4:57 AM
 * Last Modified at: 03/05/20, 4:47 AM
 */

import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import { ThemeProvider } from 'styled-components'
import utils from './utils'
import { themes } from '../../themes'
import { validateInnerHTML, useDocumentWidth, stripHtml } from '../../utils'
import {
  Wrapper,
  Label,
  ErrorMessage,
  HelperText,
  InputBody,
  ContentWrapper,
  LabelWrapper,
  InputComponents,
  TooltipWrapper
} from '../../styles'
import { PreText, PostText, CaptionText } from './styles'
import { Tooltip } from '../Tooltip'
import getInput from './input'

const TextInput = (props) => {
  const {
    labelText,
    corner,
    labelMargin,
    id,
    type,
    pattern,
    onChange,
    autoFormat,
    minValue,
    maxValue,
    minLength,
    maxLength,
    checksum,
    labelIsHTML,
    currency,
    metric,
    value,
    width,
    color,
    mask,
    required,
    disabled,
    inputRef,
    customError,
    hasTooltip,
    tooltipTitle,
    tooltipBody,
    tooltipHTML,
    tooltipPlacement,
    tooltipWidth,
    placeholder,
    hidePlaceholder,
    displayVariant,
    displayType,
    isControlledInput,
    themeConfiguration,
    hideLabel,
    captionText,
    helperText,
    showCustomError,
    errorMessageColor,
    errorFontSize,
    preTextLeftPadding,
    extraProperties,
    precision,
    allowOnlyAlphabets,
    spanText,
    labelStyles,
    inputStyles,
    isAsterisk,
    ...otherprops
  } = props

  const { 'auto-capitalise': autoCapitalise } = extraProperties || {}
  let { floatingLabel, labelPosition, actualThreshold } = props

  // override floating label if label text length is more than 30 characters
  const floatOverride = utils.overrideFloatingLabel(
    labelText,
    floatingLabel,
    placeholder,
    labelPosition,
    actualThreshold
  )
  const inpPlaceholder = hidePlaceholder ? '' : floatOverride.inpPlaceholder
  floatingLabel = floatOverride.floatingLabel
  labelPosition = floatOverride.labelPosition

  // Get document width (reactive to window resizing).
  const docWidth = useDocumentWidth()
  let exactWidth = ''
  if (themeConfiguration && themeConfiguration.exactWidth) {
    exactWidth = themeConfiguration.exactWidth[width]
  }

  const { error: e = false, error_message: em = '' } = customError
  const [hasError, setError] = useState(e)
  const [errorMsg, setErrorMsg] = useState(em)
  const [hasContent, setContent] = useState(!!value)
  const [hasFocus, setFocus] = useState(false)
  const [
    autoCapitaliseFirstLetterFirstTime,
    setAutoCapitaliseFirstLetterFirstTime
  ] = useState(autoCapitalise)
  // For handling input masking
  const [hasMask, setMask] = useState(false)
  const [clearMode, setClearMode] = useState(false)
  const hasClear = useRef(false)

  useEffect(() => {
    if (showCustomError) {
      setErrorMsg(customError.error_message)
      setError(customError.error)
    }
  }, [value, customError.error_message])
  useEffect(() => {
    if (value && mask && mask.mask_char) {
      setClearMode(true)
      setMask(true)
    }
  }, [])

  useEffect(() => {
    if (!customError.error && !hasError) {
      setError(false)
      setErrorMsg('')
    }
  }, [customError])

  useEffect(() => {
    setError(customError.error);
    setErrorMsg(customError.error_message);
  }, [customError.error, customError.error_message]);
  
  useEffect(() => {
    const { matchingObjectId } = props
    if (matchingObjectId) {
      setError(false)
      setErrorMsg('')
    }
  })

  // Finds out if the input should be cleared.
  const shouldClearInput = (v) => {
    if (clearMode && hasMask && value && value.length - v.length >= 1) {
      setClearMode(false)
      return true
    }
    return false
  }

  // If the Masked input is present, do not allow user to input anything else
  const shouldPreventInput = () => {
    if (clearMode && hasMask && value) {
      return true
    }
    return false
  }

  // check for auto format or masking
  const { value: _mask } = autoFormat
  const maskedValue = utils.getMask(_mask)

  let valueProps = { defaultValue: value }
  // If input has Mask and clearMode is false, reset the value
  if (!clearMode && hasMask) {
    valueProps.defaultValue = ''
    if (!_mask && !hasClear.current) {
      valueProps.value = ''
      hasClear.current = true
    }
  }
  if (isControlledInput) {
    valueProps = { value }
  }

  const handleChange = (event) => {
    const { value: v } = event.target
    let value = v

    setContent(!!v)

    if (shouldClearInput(v)) {
      value = ''
    }
    if (v && autoCapitaliseFirstLetterFirstTime) {
      setAutoCapitaliseFirstLetterFirstTime(false)
      event.target.value = v[0].toUpperCase() + v.slice(1)
    }
    if (v && currency && currency.format) {
      const { format, preserveFormattingInCb, decimalAllowed } = currency

      let inputVal, formattedValue

      if (decimalAllowed && v.includes('.')) {
        formattedValue = utils.parseInputwithDecimal(v, format)
        inputVal = utils.removeCurrencyFormat(v, true)
      } else {
        inputVal = utils.removeCurrencyFormat(v)
        formattedValue = utils.formatCurrency(inputVal, format)
      }

      if (preserveFormattingInCb) {
        value = formattedValue
      } else {
        value = inputVal
      }
      event.target.value = formattedValue
    }

    const { error, errorMessage } = utils.validateInput(props, value)
    if ((!!v && hasError && errorMessage) || (error && !showCustomError)) {
      setError(true)
      setErrorMsg(errorMessage)
    } else {
      setError(false)
      setErrorMsg('')
    }
    onChange(value, {
      error,
      error_message: errorMessage
    })
  }

  const handleKeyDown = (e) => {
    if (allowOnlyAlphabets && !utils.allowOnlyAlphabets(e)) {
      e.preventDefault()
      e.stopPropagation()
      return
    }
    // incase of maxlength restriction just skiping this check
    if (maxLength && maxLength.restrict) {
      return
    }
    // if currency formatting is applicable, do not allow any non numeric character input by user
    const { decimalAllowed } = currency
    const allowDot = decimalAllowed && e.key === '.'
    if (currency && currency.format) {
      if (
        e.key.length === 1 &&
        !parseInt(e.key) &&
        e.key !== '0' &&
        !allowDot
      ) {
        e.preventDefault()
        e.stopPropagation()
        return
      }
    }

    if (e.key.length === 1 && shouldPreventInput()) {
      e.preventDefault()
      e.stopPropagation()
      return
    }

    if (hasError && errorMsg) {
      return
    }

    if (utils.isValidKey(e) || (allowDot && e.key === '.')) {
      setError(false)
      setErrorMsg('')
    }
  }

  const handleKeyUp = (e) => {
    // Focus out the input when the field clears in case of Masking
    if (hasClear.current && e.keyCode === 8) {
      setFocus(false)
    }
    if (props.onKeyUp) {
      props.onKeyUp(e)
    }
  }

  const { preText, postText } = utils.getMetrics(metric)
  let displayMaskedInput = _mask
  if ((currency && currency.format) || clearMode) {
    displayMaskedInput = false
  }

  const handleFocus = (e) => {
    const { mustMatch, matchQuestionAnswer } = props
    if (mustMatch && matchQuestionAnswer && value) {
      const { error, errorMessage } = utils.validateInput(props, value)
      if (errorMessage || error) {
        setError(true)
        setErrorMsg(errorMessage)
      } else {
        setError(false)
        setErrorMsg('')
      }
    }
    setFocus(true)
    if (props.onFocus) {
      props.onFocus(e)
    }
  }

  const appendZeros = (value, length) => {
    for (let i = 0; i < length; i++) {
      value += '0'
    }
    return value
  }

  const handleBlur = (e) => {
    const { decimalAllowed } = currency
    let value = e.target.value
    let [beforeDec, afterDec = ''] = value.split('.')
    const shouldAppendZeros = decimalAllowed && value && precision
    if (shouldAppendZeros) {
      afterDec = appendZeros(afterDec || '', precision.value - afterDec.length)
      value = beforeDec + '.' + afterDec
    }
    e.target.value = value
    if (props.onBlur) {
      props.onBlur(e)
    }
    setFocus(false)
  }

  const inlineTooltip = hasTooltip && !floatingLabel && labelPosition === 'top'

  const tooltipPosition = inlineTooltip ? 'label' : tooltipPlacement || 'field'

  // text-area height override required when passing row config
  const hasRows = !!otherprops.rows

  // Remove this when theme is passed from all the clients
  const theme = themeConfiguration || themes[color]

  // ARIA specific props
  let ariaProps = {
    'aria-label': stripHtml(labelText),
    'aria-required': required && required.value
  }
  const ariaDescibebyErrorId = `${id}-error`
  if (hasError) {
    ariaProps = {
      ...ariaProps,
      'aria-invalid': hasError,
      'aria-describedby': ariaDescibebyErrorId
    }
  }

  const showHelperText = helperText && (hasFocus || disabled) && !hasError

  const HelperTextComponent = (
    <HelperText
      id={ariaDescibebyErrorId}
      data-testid='helper-text'
      aria-live='polite'
    >
      {helperText}
    </HelperText>
  )

  const ErrorComponent = (
    <ErrorMessage
      id={ariaDescibebyErrorId}
      data-testid='error-message'
      aria-live='polite'
      questionId={id}
    >
      {errorMsg}
    </ErrorMessage>
  )

  const _labelStyles = labelStyles || {}

  return (
    <ThemeProvider
      theme={{
        hasError,
        floatingLabel,
        labelPosition,
        corner,
        labelMargin,
        preText,
        postText,
        hasContent: hasContent || value || hasFocus,
        width: exactWidth || width,
        color,
        disabled,
        hideLabel,
        errorMessageColor,
        preTextLeftPadding,
        spanText,
        inputStyles,
        errorFontSize,
        hasTooltip,
        hasRows,
        ...theme
      }}
    >
      <Wrapper
        name='wrapper'
        displayVariant={displayVariant}
        width={hasTooltip && tooltipPosition === 'field' ? 'XL' : (exactWidth || width)}
        inlineTooltip={inlineTooltip}
        questionId={id}
        hasInfoIcon={hasTooltip && tooltipPosition === 'field'}
      >
        <ContentWrapper name='content-wrapper'>
          <InputBody data-testid='input-body' docWidth={docWidth}>
            <InputComponents
              id={`text-input-field-${id}`}
              name='input-components'
              docWidth={docWidth}
            >
              {preText && <PreText data-testid='pre-text'>{preText}</PreText>}
              {getInput({
                id,
                inputRef,
                autoFormat,
                displayMaskedInput,
                hasError,
                maskedValue,
                labelText,
                handleChange,
                handleKeyDown,
                handleKeyUp,
                handleFocus,
                handleBlur,
                inpPlaceholder,
                valueProps,
                otherprops,
                ariaProps,
                type,
                disabled,
                maxLength
              })}
              {postText && (
                <PostText data-testid='post-text'>{postText}</PostText>
              )}
            </InputComponents>
            <LabelWrapper
              id={`text-input-label-${id}`}
              name='label-wrapper'
              type={type}
            >
              {floatingLabel && preText && (
                <PreText name='pre-text'>{preText}</PreText>
              )}
              <Label
                name='label'
                htmlFor={id}
                data-testid='input-label'
                isAsterisk={isAsterisk}
                {..._labelStyles}
              >
                <span
                  dangerouslySetInnerHTML={{
                    __html: validateInnerHTML(labelText, isAsterisk)
                  }}
                />
              </Label>
              {hasTooltip && tooltipPosition === 'label' && (
                <TooltipWrapper name='tool-tip-wrapper' position='inline'>
                  <Tooltip
                    tooltipTitle={tooltipTitle}
                    tooltipBody={tooltipBody}
                    tooltipHTML={tooltipHTML}
                    popperWidth={tooltipWidth}
                  />
                </TooltipWrapper>
              )}
            </LabelWrapper>
            {/* check if tooltip is present then check if it's not inline */}
            {hasTooltip && tooltipPosition === 'field' && (
              <TooltipWrapper name='tool-tip-wrapper' data-testid='tool-tip'>
                <Tooltip
                  name='tool-tip'
                  tooltipTitle={tooltipTitle}
                  tooltipBody={tooltipBody}
                  tooltipHTML={tooltipHTML}
                  popperWidth={tooltipWidth}
                />
              </TooltipWrapper>
            )}
          </InputBody>
          {captionText && (
            <CaptionText
              dangerouslySetInnerHTML={{
                __html: validateInnerHTML(captionText)
              }}
            />
          )}
        </ContentWrapper>

        {showHelperText ? HelperTextComponent : ErrorComponent}
      </Wrapper>
    </ThemeProvider>
  )
}

TextInput.defaultProps = {
  type: 'text',
  value: '',
  disabled: false,
  floatingLabel: true,
  labelPosition: 'left',
  pattern: {
    value: '',
    error_message: ''
  },
  maxLength: {
    value: '',
    error_message: 'Please enter 10 or less characters.'
  },
  minLength: {
    value: '',
    error_message: 'Please enter 3 or more characters.'
  },
  autoFormat: {
    type: '',
    value: '',
    error_message: ''
  },
  corner: 'round',
  labelMargin: 'min',
  labelIsHTML: false,
  currency: {
    format: '',
    preserveFormattingInCb: false
  },
  metric: {
    display_position: 'prefix',
    value: '',
    postfix: ''
  },
  color: 'blue',
  width: 'L',
  required: {},
  customError: {},
  isControlledInput: false,
  hideLabel: false,
  minValue: '',
  maxValue: '',
  showCustomError: false,
  hidePlaceholder: false,
  isAsterisk: false
}

TextInput.propTypes = {
  /**
   * Default input type
   */
  type: PropTypes.oneOf(['text', 'password']),
  /**
   * Default value
   */
  value: PropTypes.string,
  /**
   *  `id` is used for input's label.
   */
  id: PropTypes.string.isRequired,
  /**
   * Ref for Text Input.
   */
  inputRef: PropTypes.object,
  /**
   * Determines if text input field is disabled or not.
   */
  disabled: PropTypes.bool,
  /**
   * Determines the behavior of label
   */
  floatingLabel: PropTypes.bool,
  /**
   * When label is not floating, then position of label could either be
   * `top` or `left`. If `floatingLabel` is true, then labelPosition is ignored.
   */
  labelPosition: PropTypes.oneOf(['top', 'left']),
  /**
   * The text to be displayed for the input field.
   */
  labelText: PropTypes.string.isRequired,
  /**
   * Placeholder text for the input field.
   */
  placeholder: PropTypes.string.isRequired,
  /**
   * To hide Placeholder text for the input field.
   */
  hidePlaceholder: PropTypes.bool,
  /**
   * A input change handler to exchange value entered by the end user.
   */
  onChange: PropTypes.func.isRequired,
  /**
   * An object containing the regular expression (in string format) and
   * an error message associated with the validation.
   */
  pattern: PropTypes.shape({
    value: PropTypes.string,
    error_message: PropTypes.string
  }),
  /**
   * An object containing maximum number of character allowed in the input and
   * an error message associated with the validation.
   */
  maxLength: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    error_message: PropTypes.string
  }),
  /**
   * An object containing maximum number of character allowed in the input and
   * an error message associated with the validation.
   */
  minLength: PropTypes.shape({
    value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    error_message: PropTypes.string
  }),
  /**
   * Auto format also considered as masked input, allows the user input to be
   * formatted in given format.
   */
  autoFormat: PropTypes.shape({
    type: PropTypes.string,
    value: PropTypes.string,
    error_message: PropTypes.string
  }),
  /**
   * Determines the appearance of edge of the box.
   */
  corner: PropTypes.oneOf(['round', 'sharp']),
  /**
   * It refers to the gap between label and the input field. It is applicable only
   * for fixed label when label appears on the left side.
   */
  labelMargin: PropTypes.oneOf(['min', 'max']),
  /**
   * Indicate if the label text is html text or plain text.
   */
  labelIsHTML: PropTypes.bool,
  /**
   * An object indicating if the input field needs to format the user input in currency format.
   * The value of format can be any internationalization code, e.g. en-US.
   * If user inputs: 12345, the input will show: 123,45
   * If `preserveFormattingInCb` is true, the callback will be executed with formatted value (123,45),
   * if it is false, the callback will be executed with non-formatted value(12345).
   */
  currency: PropTypes.shape({
    format: PropTypes.string,
    preserveFormattingInCb: PropTypes.bool
  }),
  /**
   * Shows pre and post text, useful for showing metrics, thus named.
   */
  metric: PropTypes.shape({
    display_position: PropTypes.oneOf(['prefix', 'postfix']),
    value: PropTypes.string,
    postfix: PropTypes.string
  }),
  /**
   * Width of the input field
   */
  width: PropTypes.oneOf(['S', 'M', 'L', 'XL']),
  /**
   * An object to specifiy whether the input is a required field or not.
   */
  required: PropTypes.shape({
    value: PropTypes.bool,
    error_message: PropTypes.string
  }),
  /**
   * If present, initialise the error state with this object.
   */
  customError: PropTypes.shape({
    error: PropTypes.bool,
    error_message: PropTypes.string
  }),
  /**
   * Theme color.
   */
  color: PropTypes.string,
  /**
   * Determines the display of input field.
   * When `displayVariant` is not given, then the input field will be render in `block` (in next line)
   * If `displayVariant` is given as `inline`, then the input fields will be render in
   * side by side (in the same line).
   */
  displayVariant: PropTypes.string,
  /**
   * Displays text keypad or numeric keypad in mobile devices
   */
  displayType: PropTypes.oneOf(['text', 'numeric']),
  /**
   * Displays value passed as the prop (makes it a controlled input)
   */
  isControlledInput: PropTypes.bool,
  /**
   * Theme configuration object
   */
  themeConfiguration: PropTypes.object,
  /**
   * hides label
   */
  hideLabel: PropTypes.bool,
  /**
   * The text to be displayed under input field.
   */
  captionText: PropTypes.string,
  /**
   * The text to be displayed under input field as assistive text.
   */
  helperText: PropTypes.string,
  /**
   * Determines the color of error message.
   */
  errorMessageColor: PropTypes.string,
  /**
   * applies padding-left when labelPosition is top & floatingLabel is false
   */
  preTextLeftPadding: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  /**
   * Indicate if the input is required or not.
   */
  isAsterisk: PropTypes.bool
}
export default TextInput
